import type { UiMode } from '@/composables/useUiMode'

/*
 * The shared Standard/Advanced presentation helpers (#1749). Screens print machine values (tool names, error
 * reasons, enum values, person IDs) through these instead of straight from the API, so one mode decides what a
 * founder sees. Advanced always gives back exactly what the screen printed before; Standard gives plain words.
 * Presentation only: nothing here changes what is stored, sent or matched on.
 */

/** Vendors whose display name is not just their key capitalised. */
const VENDOR_NAMES: Record<string, string> = {
    os: 'ZeroHuman',
    github: 'GitHub',
    gitlab: 'GitLab',
    bitbucket: 'Bitbucket',
    slack: 'Slack',
    linear: 'Linear',
    jira: 'Jira',
    notion: 'Notion',
    google: 'Google',
    gmail: 'Gmail',
    microsoft: 'Microsoft',
    hubspot: 'HubSpot',
    openai: 'OpenAI',
    http: 'HTTP',
}

/** `some_vendor` → "Some vendor": the fallback for a vendor without a known display name. */
function words(value: string): string {
    const spaced = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : spaced
}

/** A tool's name as the screen should print it: the vendor's display name in Standard, the raw name in Advanced. */
export function toolLabel(toolName: string, mode: UiMode): string {
    if (mode === 'advanced') {
        return toolName
    }
    const vendor = toolName.split('.')[0]?.trim().toLowerCase() ?? ''
    return VENDOR_NAMES[vendor] ?? (words(vendor) || 'Tool')
}

/** `merge_pull_request`, `list-issues`, `createPage` → "merge pull request", "list issues", "create page". */
function actionWords(value: string): string {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
}

/**
 * A tool where a founder allows or revokes that one tool: Standard follows the vendor's display name with the action
 * in words ("GitHub: merge pull request"), so two tools of one vendor never read alike; Advanced gives the raw name.
 * Nothing is renamed: the words are the tool's own name, without its dots and underscores.
 */
export function toolActionLabel(toolName: string, mode: UiMode): string {
    const vendor = toolLabel(toolName, mode)
    if (mode === 'advanced') {
        return vendor
    }
    const action = actionWords(toolName.split('.').slice(1).join('.'))
    return action ? `${vendor}: ${action}` : vendor
}

/** A machine error code: lower snake_case with at least one underscore, such as `workspace_repo_no_clone_address`. */
const CODE = '[a-z][a-z0-9]*(?:_[a-z0-9]+)+'
const BARE_CODE = new RegExp(`^${CODE}$`)
const LEADING_CODE = new RegExp(`^${CODE}\\s*:\\s*`)

/** What Standard says for a reason that is nothing but a code. */
export const STOPPED_WITH_AN_ERROR = 'This run stopped with an error.'

/** A run's failure reason: Standard drops a leading `code:` prefix, Advanced keeps the whole reason. */
export function errorReason(reason: string, mode: UiMode): string {
    if (mode === 'advanced') {
        return reason
    }
    const trimmed = reason.trim()
    if (BARE_CODE.test(trimmed)) {
        return STOPPED_WITH_AN_ERROR
    }
    const sentence = trimmed.replace(LEADING_CODE, '')
    return sentence || STOPPED_WITH_AN_ERROR
}

/** An enum value as words in Standard, exactly as stored in Advanced. */
export function enumLabel(value: string, mode: UiMode): string {
    return mode === 'advanced' ? value : words(value)
}

/** The text a person is shown by, if it has any. */
function present(value: string | null | undefined): string | null {
    const trimmed = value?.trim()
    return trimmed ? trimmed : null
}

/** A person: name, else role title, else "Unknown member" in Standard (never the ID); Advanced may fall back to the ID. */
export function personLabel(
    person: { id: string; name?: string | null; roleTitle?: string | null },
    mode: UiMode,
): string {
    const named = present(person.name) ?? present(person.roleTitle)
    if (named) {
        return named
    }
    return mode === 'advanced' ? person.id : 'Unknown member'
}

/** A task: its name, else "Untitled task" in Standard (never the slug); Advanced falls back to the slug, as before. */
export function taskLabel(task: { slug?: string | null; name?: string | null }, mode: UiMode): string {
    if (mode === 'advanced') {
        return task.name || task.slug || ''
    }
    return present(task.name) ?? 'Untitled task'
}

/**
 * A link's address without its scheme and host, as a label: `https://git.example.com/acme/app/issues/95` →
 * `acme/app/issues/95`. Any host alike, so a screen names no vendor's address (#589). An address that does not
 * parse, or has no path, is shown as written.
 */
export function addressLabel(address: string): string {
    try {
        const path = new URL(address).pathname.replace(/^\/+/, '')
        return path === '' ? address : decodeURI(path)
    } catch {
        return address
    }
}
